import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Link, useRoute, useLocation } from 'wouter'
import { ArrowLeft, Share2, Eye, MessageSquare, Calendar, DollarSign, Tag, FileText, Bookmark, BookmarkCheck, ExternalLink, Download, Paperclip, UserPlus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useAuth } from '@/contexts/auth-context'
import toast from 'react-hot-toast'
import { generateRfpPdf, generateRfpPdfSimple, generateResponsePdf, generateResponsePdfSimple, generateEmergencyResponsePdf, RfpPdfData, ResponsePdfData } from '@/lib/pdf-utils'
import { ContactInfo } from '@/components/contact-info'
import { Messaging } from '@/components/messaging'
import { SubscriptionModal } from '@/components/subscription-modal'
import { LinkedInInviteModal } from '@/components/linkedin-invite-modal'
import { buildApiUrl } from '@/config/api'
import { trackRfpShared } from '../components/intercom'
import { captureError } from '../lib/sentry'

interface Rfp {
  id: string
  title: string
  slug: string
  description: string
  requirements: string[]
  status: string
  category: string
  workType: 'REMOTE' | 'ONSITE' | 'HYBRID'
  country?: string
  budgetMin: number
  budgetMax: number
  budgetCurrency: string
  deadline: string
  views: number
  responses: number
  linkedinShares: number
  attachments: string[]
  externalLinks: string[]
  createdAt: string
  updatedAt: string
  user: {
    id: string
    name: string
    email: string
    company: string
  }
}

// Helper functions for file handling
const getFilenameFromKey = (key: string): string => {
  // Extract filename from S3 key format: rfp-attachments/userId/rfpId/timestamp-filename
  const parts = key.split('/')
  const lastPart = parts[parts.length - 1]
  // Remove timestamp prefix if present (format: timestamp-filename)
  const timestampMatch = lastPart.match(/^\d+-(.+)$/)
  return timestampMatch ? timestampMatch[1] : lastPart
}

const getFileExtension = (filename: string): string => {
  const parts = filename.split('.')
  return parts.length > 1 ? parts[parts.length - 1] : ''
}

const getFileIcon = (filename: string) => {
  const ext = getFileExtension(filename).toLowerCase()
  switch (ext) {
    case 'pdf':
      return <FileText className="h-5 w-5 text-red-500" />
    case 'doc':
    case 'docx':
      return <FileText className="h-5 w-5 text-blue-500" />
    case 'xls':
    case 'xlsx':
      return <FileText className="h-5 w-5 text-green-500" />
    case 'txt':
      return <FileText className="h-5 w-5 text-gray-500" />
    case 'png':
    case 'jpg':
    case 'jpeg':
      return <FileText className="h-5 w-5 text-purple-500" />
    default:
      return <FileText className="h-5 w-5 text-gray-500" />
  }
}

export function RfpDetail() {
  const { user } = useAuth()
  const [, setLocation] = useLocation()
  const [, params] = useRoute('/rfp/:identifier')
  const identifier = params?.identifier
  
  const [rfp, setRfp] = useState<Rfp | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [userResponse, setUserResponse] = useState<{
    hasResponded: boolean
    response?: {
      id: string
      status: string
      createdAt: string
    }
  } | null>(null)
  const [acceptedResponses, setAcceptedResponses] = useState<any[]>([])
  const [isInWatchlist, setIsInWatchlist] = useState(false)
  const [isWatchlistLoading, setIsWatchlistLoading] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [subscriptionStatus, setSubscriptionStatus] = useState<{
    hasActiveSubscription: boolean
    subscriptionPlan: string
  } | null>(null)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  const [shareContent, setShareContent] = useState('')
  const [showLinkedInInviteModal, setShowLinkedInInviteModal] = useState(false)

  useEffect(() => {
    const fetchRfp = async () => {
      if (!identifier) {
        setError('RFP identifier is required')
        setIsLoading(false)
        return
      }

      try {
        const token = localStorage.getItem('token')
        const headers: Record<string, string> = {}
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }

        // Try protected first if logged in, then fallback to public
        let response = await fetch(
          token ? buildApiUrl(`/api/rfps/${identifier}`) : buildApiUrl(`/api/rfps/${identifier}/public`),
          { headers }
        )

        if (!response.ok && token && (response.status === 401 || response.status === 404)) {
          // Fallback to public view (e.g., sample RFPs or non-owned RFPs)
          response = await fetch(buildApiUrl(`/api/rfps/${identifier}/public`), { headers: {} })
        }

        if (response.ok) {
          const data = await response.json()
          setRfp(data.data)
          const rfpData = data.data

          // If user is authenticated, check auxiliary data
          if (token && user && rfpData) {
            try {
              const responseCheck = await fetch(buildApiUrl(`/api/responses/check/${rfpData.id}`), {
                headers: { 'Authorization': `Bearer ${token}` },
              })
              if (responseCheck.ok) {
                const responseData = await responseCheck.json()
                setUserResponse(responseData.data)
              }
            } catch (error) {
              console.error('Error checking user response:', error)
            }

            // Check if RFP is in user's watchlist
            try {
              const watchlistResponse = await fetch(buildApiUrl(`/api/watchlist/check-batch`), {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ rfpIds: [rfpData.id] }),
              })
              if (watchlistResponse.ok) {
                const watchlistData = await watchlistResponse.json()
                setIsInWatchlist(watchlistData.data[rfpData.id] || false)
              }
            } catch (error) {
              console.error('Error checking watchlist status:', error)
            }

            // Fetch accepted responses for contact information (only if user is authenticated)
            if (token && user) {
              try {
                const acceptedResponse = await fetch(buildApiUrl(`/api/responses/rfp/${rfpData.id}/accepted`), {
                  headers: { 'Authorization': `Bearer ${token}` },
                })
                if (acceptedResponse.ok) {
                  const acceptedData = await acceptedResponse.json()
                  setAcceptedResponses(acceptedData.data)
                } else if (acceptedResponse.status === 404) {
                  // No accepted responses found, this is normal
                  setAcceptedResponses([])
                }
              } catch (error) {
                console.error('Error fetching accepted responses:', error)
              }
            }

            // Check subscription status for attachment access
            try {
              const subscriptionResponse = await fetch(buildApiUrl('/api/subscription/status'), {
                headers: { 'Authorization': `Bearer ${token}` },
              })
              if (subscriptionResponse.ok) {
                const subscriptionData = await subscriptionResponse.json()
                setSubscriptionStatus(subscriptionData.data)
              }
            } catch (error) {
              console.error('Error fetching subscription status:', error)
            }
          }
        } else if (response.status === 404) {
          setError('RFP not found')
        } else {
          setError('Failed to load RFP')
        }
      } catch (error) {
        console.error('Error fetching RFP:', error)
        setError('Failed to load RFP')
      } finally {
        setIsLoading(false)
      }
    }

    fetchRfp()
  }, [identifier, user])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PUBLISHED':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'DRAFT':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'CLOSED':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
    }
  }

  const handleFileDownload = async (attachmentKey: string, filename: string) => {
    if (!rfp) return
    
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Authentication required')
        return
      }

      toast.loading(`Preparing ${filename} for download...`)

      const response = await fetch(buildApiUrl(`/api/files/download/${rfp.id}/${attachmentKey}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to download file')
      }

      const data = await response.json()
      
      // Open the pre-signed URL in a new tab to download the file
      window.open(data.data.downloadUrl, '_blank')
      
      toast.dismiss()
      toast.success(`${filename} download started!`)
    } catch (error) {
      toast.dismiss()
      console.error('Download error:', error)
      toast.error(`Failed to download file: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const exportToPdf = async () => {
    if (!rfp) return
    
    try {
      const pdfData: RfpPdfData = {
        id: rfp.id,
        title: rfp.title,
        description: rfp.description,
        requirements: rfp.requirements,
        status: rfp.status,
        category: rfp.category,
        workType: rfp.workType,
        country: rfp.country,
        budgetMin: rfp.budgetMin,
        budgetMax: rfp.budgetMax,
        budgetCurrency: rfp.budgetCurrency,
        deadline: rfp.deadline,
        createdAt: rfp.createdAt,
        user: {
          name: rfp.user.name,
          company: rfp.user.company,
        },
      }

      // Try the HTML-based method first, fallback to simple method
      try {
        await generateRfpPdf(pdfData)
        toast.success('PDF exported successfully!')
      } catch (error) {
        console.warn('HTML-based PDF generation failed, using simple method:', error)
        generateRfpPdfSimple(pdfData)
        toast.success('PDF exported successfully!')
      }
    } catch (error) {
      console.error('Error exporting PDF:', error)
      toast.error('Failed to export PDF')
    }
  }

  const exportResponseToPdf = async () => {
    if (!userResponse?.response) {
      toast.error('No response data available')
      return
    }
    
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Authentication required')
        return
      }

      toast.loading('Generating response PDF...', { id: 'response-pdf' })

      // Fetch response data from backend
      const response = await fetch(buildApiUrl(`/api/pdf/response/${userResponse.response.id}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Backend error:', errorText)
        throw new Error(`Failed to fetch response data: ${response.status} ${response.statusText}`)
      }

      const result = await response.json()
      
      if (!result.success || !result.data) {
        throw new Error('Invalid response data from server')
      }

      const responseData: ResponsePdfData = result.data

      // Validate required data
      if (!responseData.rfp || !responseData.responder) {
        throw new Error('Missing required response data')
      }

      // Try the HTML-based method first, fallback to simple method
      try {
        console.log('Attempting HTML-based PDF generation...')
        await generateResponsePdf(responseData)
        toast.success('Response PDF exported successfully!', { id: 'response-pdf' })
      } catch (htmlError) {
        console.warn('HTML-based response PDF generation failed, using simple method:', htmlError)
        try {
          console.log('Attempting simple PDF generation...')
          generateResponsePdfSimple(responseData)
          toast.success('Response PDF exported successfully!', { id: 'response-pdf' })
        } catch (simpleError) {
          console.error('Simple PDF generation also failed, trying emergency fallback:', simpleError)
          try {
            console.log('Attempting emergency PDF generation...')
            generateEmergencyResponsePdf(responseData)
            toast.success('Basic response PDF exported successfully!', { id: 'response-pdf' })
          } catch (emergencyError) {
            console.error('Emergency PDF generation also failed:', emergencyError)
            throw new Error('All PDF generation methods failed')
          }
        }
      }
    } catch (error) {
      console.error('Error exporting response PDF:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      toast.error(`Failed to export response PDF: ${errorMessage}`, { id: 'response-pdf' })
    }
  }

  const publishRfp = async () => {
    if (!rfp || !user) return
    
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Authentication required')
        return
      }

      const response = await fetch(buildApiUrl(`/api/rfps/${rfp.id}/publish`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      })

      const data = await response.json()

      if (data.success) {
        toast.success('RFP published successfully!')
        // Refresh the RFP data
        const refreshResponse = await fetch(buildApiUrl(`/api/rfps/${rfp.id}`), {
          headers: { 'Authorization': `Bearer ${token}` },
        })
        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json()
          setRfp(refreshData.data)
        }
      } else {
        toast.error(data.error || 'Failed to publish RFP')
      }
    } catch (error) {
      console.error('Error publishing RFP:', error)
      toast.error('Failed to publish RFP')
    }
  }

  const shareOnLinkedIn = async () => {
    if (!rfp || !user) return

    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Authentication required')
        return
      }

      toast.loading('Generating compelling LinkedIn content...', { id: 'linkedin-share' })

      // First, track the share and get RFP data
      const shareResponse = await fetch(buildApiUrl(`/api/rfps/${rfp!.id}/share-linkedin`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!shareResponse.ok) {
        throw new Error('Failed to track LinkedIn share')
      }

      const shareData = await shareResponse.json()

      // Generate AI-powered LinkedIn content
      let shareText = ''
      try {
        const aiResponse = await fetch(buildApiUrl('/api/ai/generate-linkedin-share'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            rfp: shareData.data.rfp,
            companyName: shareData.data.companyName,
          }),
        })

        if (aiResponse.ok) {
          const aiData = await aiResponse.json()
          shareText = aiData.data.content
          toast.success('AI-generated content ready!', { id: 'linkedin-share' })
        } else {
          throw new Error('AI generation failed')
        }
      } catch (aiError) {
        console.warn('AI content generation failed, using fallback:', aiError)
        // Fallback to enhanced basic content if AI fails
        shareText = `🚀 Exciting opportunity alert! We're looking for top-tier vendors for our latest project.\n\n📋 ${rfp!.title}\n\n${rfp!.description.substring(0, 150)}${rfp!.description.length > 150 ? '...' : ''}\n\n💰 Budget: $${rfp!.budgetMin.toLocaleString()} - $${rfp!.budgetMax.toLocaleString()} ${rfp!.budgetCurrency}\n📅 Deadline: ${new Date(rfp!.deadline).toLocaleDateString()}\n\nInterested? Check out the full details and submit your proposal!\n\n#RFP #Business #Procurement #Vendors`
        toast.success('Share content ready!', { id: 'linkedin-share' })
      }

      // Store the AI-generated content and show the share modal
      setShareContent(shareText)
      setShowShareModal(true)
      
      // Track the LinkedIn share event
      trackRfpShared({
        id: rfp.id,
        title: rfp.title,
        platform: 'LinkedIn'
      })
      
      // Update the RFP data with new share count
      if (shareData.success) {
        setRfp(prev => prev ? { ...prev, linkedinShares: shareData.data.shares } : null)
      }
    } catch (error) {
      console.error('Error sharing on LinkedIn:', error)
      captureError(error as Error, { 
        context: 'linkedin_sharing', 
        rfpId: rfp?.id,
        userId: user?.id 
      })
      toast.error('Failed to share on LinkedIn', { id: 'linkedin-share' })
    }
  }

  const unpublishRfp = async () => {
    if (!rfp || !user) return
    
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Authentication required')
        return
      }

      const response = await fetch(buildApiUrl(`/api/rfps/${rfp.id}/unpublish`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      })

      const data = await response.json()

      if (data.success) {
        toast.success('RFP set to draft successfully!')
        // Refresh the RFP data
        const refreshResponse = await fetch(buildApiUrl(`/api/rfps/${rfp.id}`), {
          headers: { 'Authorization': `Bearer ${token}` },
        })
        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json()
          setRfp(refreshData.data)
        }
      } else {
        toast.error(data.error || 'Failed to set RFP to draft')
      }
    } catch (error) {
      console.error('Error unpublishing RFP:', error)
      toast.error('Failed to set RFP to draft')
    }
  }

  const editRfp = () => {
    if (!rfp) return
    setLocation(`/edit-rfp/${rfp.id}`)
  }

  const withdrawResponse = async () => {
    if (!userResponse?.response) return
    
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Authentication required')
        return
      }

      const response = await fetch(buildApiUrl(`/api/responses/${userResponse.response.id}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      })

      const data = await response.json()

      if (data.success) {
        toast.success('Response withdrawn successfully!')
        // Refresh the RFP data to update response count
        if (rfp) {
          const refreshResponse = await fetch(buildApiUrl(`/api/rfps/${rfp.id}`), {
            headers: { 'Authorization': `Bearer ${token}` },
          })
          if (refreshResponse.ok) {
            const refreshData = await refreshResponse.json()
            setRfp(refreshData.data)
          }
        }
        // Clear user response data
        setUserResponse({ hasResponded: false })
      } else {
        toast.error(data.error || 'Failed to withdraw response')
      }
    } catch (error) {
      console.error('Error withdrawing response:', error)
      toast.error('Failed to withdraw response')
    }
  }

  const editResponse = () => {
    if (!userResponse?.response) return
    setLocation(`/edit-response/${userResponse.response.id}`)
  }

  const toggleWatchlist = async () => {
    if (!user || !rfp) return
    
    setIsWatchlistLoading(true)
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Authentication required')
        return
      }

      const method = isInWatchlist ? 'DELETE' : 'POST'
      const response = await fetch(buildApiUrl(`/api/watchlist/${rfp.id}`), {
        method,
        headers: { 'Authorization': `Bearer ${token}` },
      })

      if (response.ok) {
        setIsInWatchlist(!isInWatchlist)
        toast.success(
          isInWatchlist 
            ? 'Removed from watchlist' 
            : 'Added to watchlist'
        )
      } else {
        const data = await response.json()
        toast.error(data.error || 'Failed to update watchlist')
      }
    } catch (error) {
      console.error('Error updating watchlist:', error)
      toast.error('Failed to update watchlist')
    } finally {
      setIsWatchlistLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center space-x-4">
          <Link href="/request-for-proposal-marketplace">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Loading RFP...</h1>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading RFP details...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !rfp) {
    return (
      <div className="space-y-8">
        <div className="flex items-center space-x-4">
          <Link href="/request-for-proposal-marketplace">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">RFP Not Found</h1>
            <p className="text-muted-foreground">
              {error || 'The RFP you are looking for does not exist or you do not have access to it.'}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-muted-foreground mb-4">
              {error || 'RFP not found'}
            </p>
            <Link href="/request-for-proposal-marketplace">
              <Button>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to RFPs
              </Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link href="/request-for-proposal-marketplace">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center space-x-2">
              <h1 className="text-3xl font-bold tracking-tight">{rfp.title}</h1>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(rfp.status)}`}>
                {rfp.status.toLowerCase()}
              </span>
              {acceptedResponses.length > 0 && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                  ✓ Accepted Response
                </span>
              )}
            </div>
            <p className="text-muted-foreground">
              Created on {new Date(rfp.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex space-x-2">
          {user && rfp.user.id !== user.id && (
            <Button 
              variant="outline" 
              onClick={toggleWatchlist}
              disabled={isWatchlistLoading}
            >
              {isInWatchlist ? (
                <>
                  <BookmarkCheck className="mr-2 h-4 w-4" />
                  Remove from Watchlist
                </>
              ) : (
                <>
                  <Bookmark className="mr-2 h-4 w-4" />
                  Add to Watchlist
                </>
              )}
            </Button>
          )}
          <Button variant="outline" onClick={exportToPdf}>
            <FileText className="mr-2 h-4 w-4" />
            Export RFP PDF
          </Button>
          {userResponse?.hasResponded && (
            <Button variant="outline" onClick={exportResponseToPdf}>
              <FileText className="mr-2 h-4 w-4" />
              Export Response PDF
            </Button>
          )}
          {rfp.user.id === user?.id && (
            <>
              <Button variant="outline" onClick={shareOnLinkedIn}>
                <Share2 className="mr-2 h-4 w-4" />
                Share on LinkedIn
              </Button>
              <Button variant="outline" onClick={() => setShowLinkedInInviteModal(true)}>
                <UserPlus className="mr-2 h-4 w-4" />
                Invite Connections
              </Button>
            </>
          )}
          {rfp.user.id !== user?.id ? (
            // User is not the RFP owner
            userResponse?.hasResponded ? (
              // User has already responded
              <div className="flex space-x-2">
                <Button variant="outline" onClick={editResponse}>
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Edit Response
                </Button>
                <Button variant="outline" onClick={withdrawResponse}>
                  Withdraw Response
                </Button>
              </div>
            ) : (
              // User hasn't responded yet
              <Link href={`/respond/${rfp.id}`}>
                <Button>
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Respond to RFP
                </Button>
              </Link>
            )
          ) : (
            // User is the RFP owner
            <Link href={`/rfp/${rfp.id}/responses`}>
              <Button>
                <MessageSquare className="mr-2 h-4 w-4" />
                View Responses
              </Button>
            </Link>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card>
            <CardHeader>
              <CardTitle>Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                {rfp.description}
              </p>
            </CardContent>
          </Card>

          {/* Requirements */}
          <Card>
            <CardHeader>
              <CardTitle>Requirements</CardTitle>
              <CardDescription>
                Specific requirements and deliverables expected from vendors.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {rfp.requirements.map((requirement, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <span className="flex-shrink-0 w-2 h-2 bg-primary rounded-full mt-2"></span>
                    <span className="text-muted-foreground">{requirement}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* External Links */}
          {rfp.externalLinks && rfp.externalLinks.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <ExternalLink className="h-5 w-5" />
                  <span>External Links</span>
                </CardTitle>
                <CardDescription>
                  External documents, specifications, and reference materials.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {rfp.externalLinks.map((link, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50">
                      <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <a
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline text-sm font-medium truncate block"
                          title={link}
                        >
                          {link}
                        </a>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                      >
                        <a
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open
                        </a>
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* File Attachments - PRO Feature */}
          {rfp.attachments && rfp.attachments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Paperclip className="h-5 w-5" />
                  <span>Attached Files</span>
                  {subscriptionStatus?.subscriptionPlan !== 'PRO' && (
                    <span className="ml-2 px-2 py-1 text-xs bg-amber-100 text-amber-800 rounded-full">
                      PRO
                    </span>
                  )}
                </CardTitle>
                <CardDescription>
                  Supporting documents, specifications, and reference materials.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {subscriptionStatus?.subscriptionPlan === 'PRO' ? (
                  <div className="space-y-3">
                    {rfp.attachments.map((attachmentKey, index) => {
                      const filename = getFilenameFromKey(attachmentKey)
                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            {getFileIcon(filename)}
                            <div>
                              <p className="font-medium text-sm">{filename}</p>
                              <p className="text-xs text-gray-500">
                                {getFileExtension(filename).toUpperCase()} file
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleFileDownload(attachmentKey, filename)}
                            className="flex items-center space-x-1"
                          >
                            <Download className="h-4 w-4" />
                            <span>Download</span>
                          </Button>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 space-y-4">
                    <div className="flex justify-center">
                      <div className="p-3 bg-amber-100 rounded-full">
                        <Paperclip className="h-6 w-6 text-amber-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">
                        {rfp.attachments.length} File{rfp.attachments.length !== 1 ? 's' : ''} Available
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Upgrade to PRO to access supporting documents and reference materials.
                      </p>
                      <Button onClick={() => setShowSubscriptionModal(true)}>
                        Upgrade to PRO
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Contact Information for Accepted Responses */}
          {acceptedResponses.length > 0 && (
            <ContactInfo
              vendor={rfp.user}
              supplier={acceptedResponses[0].user}
            />
          )}

          {/* Messaging for Accepted Responses */}
          {acceptedResponses.length > 0 && (
            <Messaging
              rfpId={rfp.id}
              otherParty={
                user?.id === rfp.user.id
                  ? acceptedResponses[0].user
                  : rfp.user
              }
            />
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Key Details */}
          <Card>
            <CardHeader>
              <CardTitle>Key Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Tag className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Category:</span>
                <span className="text-sm font-medium">{rfp.category}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="h-4 w-4 text-muted-foreground">📍</span>
                <span className="text-sm text-muted-foreground">Work Type:</span>
                <span className="text-sm font-medium">
                  {rfp.workType === 'REMOTE' ? 'Remote' : 
                   rfp.workType === 'ONSITE' ? 'On-site' : 'Hybrid'}
                </span>
              </div>
              {rfp.country && (
                <div className="flex items-center space-x-2">
                  <span className="h-4 w-4 text-muted-foreground">🌍</span>
                  <span className="text-sm text-muted-foreground">Country:</span>
                  <span className="text-sm font-medium">{rfp.country}</span>
                </div>
              )}
              <div className="flex items-center space-x-2">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Budget:</span>
                <span className="text-sm font-medium">
                  ${rfp.budgetMin.toLocaleString()} - ${rfp.budgetMax.toLocaleString()} {rfp.budgetCurrency}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Deadline:</span>
                <span className="text-sm font-medium">
                  {new Date(rfp.deadline).toLocaleDateString()}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Performance Metrics */}
          <Card>
            <CardHeader>
              <CardTitle>Performance</CardTitle>
              <CardDescription>
                Engagement metrics for this RFP.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Eye className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Views</span>
                </div>
                <span className="text-sm font-medium">{rfp.views}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Share2 className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">LinkedIn Shares</span>
                </div>
                <span className="text-sm font-medium">{rfp.linkedinShares}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Responses</span>
                </div>
                <span className="text-sm font-medium">{rfp.responses}</span>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {user ? (
                // Authenticated user actions
                <>
                  {user.id === rfp.user.id ? (
                    // RFP owner actions
                    <>
                      <Button variant="outline" className="w-full" onClick={editRfp}>
                        Edit RFP
                      </Button>
                      {rfp.status === 'DRAFT' && (
                        <Button className="w-full" onClick={publishRfp}>
                          Publish RFP
                        </Button>
                      )}
                      {rfp.status === 'PUBLISHED' && (
                        <Button variant="outline" className="w-full" onClick={unpublishRfp}>
                          Set to Draft
                        </Button>
                      )}
                      <Button variant="outline" className="w-full">
                        Duplicate
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200" 
                        onClick={shareOnLinkedIn}
                      >
                        <ExternalLink className="mr-2 h-4 w-4" />
                        Share on LinkedIn
                      </Button>
                      <Link href={`/rfp/${rfp.id}/responses`}>
                        <Button variant="outline" className="w-full">
                          <MessageSquare className="mr-2 h-4 w-4" />
                          View Responses ({rfp.responses})
                        </Button>
                      </Link>
                    </>
                  ) : (
                    // Non-owner actions
                    userResponse?.hasResponded ? (
                      // User has already responded
                      <>
                        <Button variant="outline" className="w-full" onClick={editResponse}>
                          <MessageSquare className="mr-2 h-4 w-4" />
                          Edit Response
                        </Button>
                        <Button variant="outline" className="w-full" onClick={withdrawResponse}>
                          Withdraw Response
                        </Button>
                      </>
                    ) : (
                      // User hasn't responded yet
                      <Link href={`/respond/${rfp.id}`}>
                        <Button className="w-full">
                          <MessageSquare className="mr-2 h-4 w-4" />
                          Respond to RFP
                        </Button>
                      </Link>
                    )
                  )}
                  <Button variant="outline" className="w-full" onClick={exportToPdf}>
                    <FileText className="mr-2 h-4 w-4" />
                    Export RFP PDF
                  </Button>
                  {userResponse?.hasResponded && (
                    <Button variant="outline" className="w-full" onClick={exportResponseToPdf}>
                      <FileText className="mr-2 h-4 w-4" />
                      Export Response PDF
                    </Button>
                  )}
                </>
              ) : (
                // Non-authenticated user actions
                <>
                  <Link href="/register">
                    <Button className="w-full">
                      <MessageSquare className="mr-2 h-4 w-4" />
                      Sign Up to Respond
                    </Button>
                  </Link>
                  <Link href="/login">
                    <Button variant="outline" className="w-full">
                      Login to Respond
                    </Button>
                  </Link>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* AI-Generated LinkedIn Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">🤖 AI-Generated LinkedIn Content</h3>
                  <p className="text-sm text-gray-600 mt-1">Compelling, professional content optimized for engagement</p>
                </div>
                <button 
                  onClick={() => setShowShareModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="text-sm text-gray-600 mb-2">📝 Your AI-generated LinkedIn post:</div>
                <div className="bg-white rounded border p-3 text-sm whitespace-pre-wrap font-medium text-gray-900">
                  {shareContent}
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(shareContent)
                      toast.success('Content copied to clipboard!')
                    } catch (error) {
                      toast.error('Failed to copy content')
                    }
                  }}
                  variant="outline" 
                  className="flex-1"
                >
                  📋 Copy Content
                </Button>
                
                <Button 
                  onClick={() => {
                    const rfpUrl = `${window.location.origin}/rfp/${rfp?.id}`
                    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(rfpUrl)}&text=${encodeURIComponent(shareContent)}`
                    window.open(linkedinUrl, '_blank', 'width=600,height=600,scrollbars=yes,resizable=yes')
                    setShowShareModal(false)
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  🚀 Share on LinkedIn
                </Button>
              </div>
              
              <div className="mt-4 text-xs text-gray-500 bg-blue-50 p-3 rounded">
                💡 <strong>Pro tip:</strong> The content is automatically optimized with industry hashtags, 
                compelling messaging, and call-to-actions to maximize engagement and attract qualified vendors.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        feature="attachments"
        currentPlan={subscriptionStatus?.subscriptionPlan as 'FREE' | 'PRO'}
        hasActiveSubscription={subscriptionStatus?.hasActiveSubscription || false}
      />

      {/* LinkedIn Invite Modal */}
      {rfp && (
        <LinkedInInviteModal
          isOpen={showLinkedInInviteModal}
          onClose={() => setShowLinkedInInviteModal(false)}
          rfpId={rfp.id}
          rfpTitle={rfp.title}
        />
      )}
    </div>
  )
} 